import * as React from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { z } from "zod";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  createCateringEvent,
  updateCateringEvent,
  type CateringEventSummary,
  type CateringEventServiceStyle,
  type CreateCateringEventPayload,
  type UpdateCateringEventPayload,
} from "@/lib/api/catering";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";

const SERVICE_STYLE_OPTIONS = ["on_premise", "offsite_full_service", "offsite_dropoff", "pickup"] as const;

type ServiceStyleOption = (typeof SERVICE_STYLE_OPTIONS)[number];

const CateringEventFormSchema = z.object({
  name: z.string().trim().min(1).max(160),
  eventDate: z.string().trim().min(1),
  serviceStyle: z.enum(SERVICE_STYLE_OPTIONS as [ServiceStyleOption, ...ServiceStyleOption[]]),
  currency: z.string().trim().min(1).max(8).default("USD"),
  estimatedGuests: z.string().trim().max(6).optional(),
  budget: z.string().trim().max(12).optional(),
  depositAmount: z.string().trim().max(12).optional(),
  depositDueDate: z.string().trim().optional(),
  finalPaymentDueDate: z.string().trim().optional(),
  contactName: z.string().trim().max(160).optional(),
  contactEmail: z.string().trim().email().max(160).optional().or(z.literal("")),
  contactPhone: z.string().trim().max(40).optional(),
  leadSource: z.string().trim().max(160).optional(),
  notes: z.string().trim().max(2000).optional(),
});

type CateringEventFormValues = z.infer<typeof CateringEventFormSchema>;

const DEFAULT_FORM_VALUES: CateringEventFormValues = {
  name: "",
  eventDate: "",
  serviceStyle: "on_premise",
  currency: "USD",
  estimatedGuests: "",
  budget: "",
  depositAmount: "",
  depositDueDate: "",
  finalPaymentDueDate: "",
  contactName: "",
  contactEmail: "",
  contactPhone: "",
  leadSource: "",
  notes: "",
};

const toEpochFromDateInput = (value: string | undefined | null): number | null => {
  if (!value) {
    return null;
  }
  const normalized = `${value}T00:00:00Z`;
  const timestamp = Date.parse(normalized);
  return Number.isNaN(timestamp) ? null : timestamp;
};

const toDateInputValue = (value: number | null | undefined): string => {
  if (!value) {
    return "";
  }
  return new Date(value).toISOString().slice(0, 10);
};

const parseInteger = (value: string | undefined): number | null => {
  if (!value) {
    return null;
  }
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

const parseCurrencyToMinorUnits = (value: string | undefined): number | null => {
  if (!value) {
    return null;
  }
  const normalized = value.replace(/[^0-9.-]/g, "");
  const parsed = Number.parseFloat(normalized);
  if (Number.isNaN(parsed)) {
    return null;
  }
  return Math.round(parsed * 100);
};

const formatCurrencyInput = (value: number | null | undefined): string => {
  if (value === null || value === undefined) {
    return "";
  }
  return (value / 100).toString();
};

const sanitizeText = (value: string | undefined): string | null => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
};

const mapEventToFormValues = (event: CateringEventSummary): CateringEventFormValues => ({
  name: event.name ?? "",
  eventDate: toDateInputValue(event.eventDate),
  serviceStyle: (event.serviceStyle as ServiceStyleOption) ?? "on_premise",
  currency: event.currency ?? "USD",
  estimatedGuests: event.estimatedGuests !== null && event.estimatedGuests !== undefined ? String(event.estimatedGuests) : "",
  budget: formatCurrencyInput(event.budgetCents),
  depositAmount: "",
  depositDueDate: toDateInputValue(event.depositDueAt),
  finalPaymentDueDate: toDateInputValue(event.finalPaymentDueAt),
  contactName: event.contactName ?? "",
  contactEmail: event.contactEmail ?? "",
  contactPhone: event.contactPhone ?? "",
  leadSource: event.leadSource ?? "",
  notes: event.notes ?? "",
});

type CateringEventFormDialogProps =
  | {
      mode: "create";
      open: boolean;
      onOpenChange: (open: boolean) => void;
      onSuccess?: (event: CateringEventSummary) => void;
    }
  | {
    mode: "edit";
    eventId: string;
    event: CateringEventSummary;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSuccess?: (event: CateringEventSummary) => void;
  };

export const CateringEventFormDialog: React.FC<CateringEventFormDialogProps> = (props) => {
  const { mode, open, onOpenChange, onSuccess } = props;
  const { t } = useTranslation(["events"]);
  const { toast } = useToast();
  const editEvent = mode === "edit" ? props.event : null;
  const editEventId = mode === "edit" ? props.eventId : null;

  const initialValues =
    mode === "edit" && editEvent ? mapEventToFormValues(editEvent) : DEFAULT_FORM_VALUES;

  const form = useForm<CateringEventFormValues>({
    resolver: zodResolver(CateringEventFormSchema),
    defaultValues: initialValues,
  });

  React.useEffect(() => {
    if (mode === "edit" && editEvent && open) {
      form.reset(mapEventToFormValues(editEvent));
      return;
    }
    if (mode === "create" && !open) {
      form.reset(DEFAULT_FORM_VALUES);
    }
  }, [editEvent, form, mode, open]);

  const mutation = useMutation({
    mutationFn: async (payload: CreateCateringEventPayload | UpdateCateringEventPayload) => {
      if (mode === "create") {
        return createCateringEvent(payload as CreateCateringEventPayload);
      }
      if (!editEventId) {
        throw new Error("eventId is required");
      }
      return updateCateringEvent(editEventId, payload as UpdateCateringEventPayload);
    },
    onSuccess: ({ event }) => {
      toast({
        title:
          mode === "create"
            ? t("events:toasts.createSuccess", { defaultValue: "Event created" })
            : t("events:toasts.updateSuccess", { defaultValue: "Event updated" }),
      });
      if (mode === "create") {
        form.reset(DEFAULT_FORM_VALUES);
      } else {
        form.reset(mapEventToFormValues(event));
      }
      onOpenChange(false);
      onSuccess?.(event);
    },
    onError: (error: unknown) => {
      toast({
        title:
          mode === "create"
            ? t("events:toasts.createErrorTitle", { defaultValue: "Unable to create event" })
            : t("events:toasts.updateErrorTitle", { defaultValue: "Unable to update event" }),
        description:
          error instanceof Error
            ? error.message
            : t("events:toasts.genericError", { defaultValue: "Try again in a moment." }),
        variant: "destructive",
      });
    },
  });

  const onSubmit = (values: CateringEventFormValues) => {
    const eventDate = toEpochFromDateInput(values.eventDate);
    if (!eventDate) {
      form.setError("eventDate", {
        message: t("events:form.validation.date", { defaultValue: "Choose a valid date" }),
      });
      return;
    }

    const currency = values.currency.trim().toUpperCase();
    const estimatedGuests = parseInteger(values.estimatedGuests);
    const budgetCents = parseCurrencyToMinorUnits(values.budget);
    const depositAmountCents = parseCurrencyToMinorUnits(values.depositAmount);
    const depositDueAt = toEpochFromDateInput(values.depositDueDate);
    const finalPaymentDueAt = toEpochFromDateInput(values.finalPaymentDueDate);

    const sharedPayload: Omit<CreateCateringEventPayload, "milestones"> = {
      name: values.name.trim(),
      eventDate,
      serviceStyle: values.serviceStyle as CateringEventServiceStyle,
      currency,
      estimatedGuests: estimatedGuests ?? null,
      budgetCents: budgetCents ?? null,
      depositDueAt: depositDueAt ?? null,
      finalPaymentDueAt: finalPaymentDueAt ?? null,
      contactName: sanitizeText(values.contactName),
      contactEmail: sanitizeText(values.contactEmail),
      contactPhone: sanitizeText(values.contactPhone),
      leadSource: sanitizeText(values.leadSource),
      notes: sanitizeText(values.notes),
    };

    if (mode === "create") {
      const payload: CreateCateringEventPayload = {
        ...sharedPayload,
        milestones:
          depositAmountCents && depositAmountCents > 0
            ? [
                {
                  kind: "deposit",
                  status: "pending",
                  amountCents: depositAmountCents,
                  currency,
                  dueAt: depositDueAt ?? eventDate,
                },
              ]
            : undefined,
      };
      mutation.mutate(payload);
      return;
    }

    const updatePayload: UpdateCateringEventPayload = {
      ...sharedPayload,
    };
    mutation.mutate(updatePayload);
  };

  const isSubmitting = mutation.isPending;

  const dialogTitle =
    mode === "create"
      ? t("events:form.title", { defaultValue: "Create catering event" })
      : t("events:form.editTitle", { defaultValue: "Edit event details" });
  const dialogSubtitle =
    mode === "create"
      ? t("events:form.subtitle", {
          defaultValue: "Capture the basics now. You can refine staffing, menu, and payments later.",
        })
      : t("events:form.editSubtitle", {
          defaultValue: "Update event basics, payment deadlines, and contacts.",
        });
  const submitLabel =
    mode === "create"
      ? t("events:form.actions.submit", { defaultValue: "Create event" })
      : t("events:form.actions.update", { defaultValue: "Save changes" });
  const savingLabel =
    mode === "create"
      ? t("events:form.actions.saving", { defaultValue: "Creating..." })
      : t("events:form.actions.updating", { defaultValue: "Saving..." });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription>{dialogSubtitle}</DialogDescription>
        </DialogHeader>
        <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(onSubmit)}>
          <div className="grid gap-3">
            <Label htmlFor="event-name">{t("events:form.fields.name", { defaultValue: "Event name" })}</Label>
            <Input id="event-name" disabled={isSubmitting} {...form.register("name")} />
            {form.formState.errors.name ? (
              <p className="text-sm text-rose-500">{form.formState.errors.name.message}</p>
            ) : null}
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div className="flex flex-col gap-2">
              <Label htmlFor="event-date">{t("events:form.fields.date", { defaultValue: "Event date" })}</Label>
              <Input id="event-date" type="date" disabled={isSubmitting} {...form.register("eventDate")} />
              {form.formState.errors.eventDate ? (
                <p className="text-sm text-rose-500">{form.formState.errors.eventDate.message}</p>
              ) : null}
            </div>
            <div className="flex flex-col gap-2">
              <Label>{t("events:form.fields.serviceStyle", { defaultValue: "Service style" })}</Label>
              <Controller
                control={form.control}
                name="serviceStyle"
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={(value) => field.onChange(value as ServiceStyleOption)}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger className="border-neutral-200 dark:border-neutral-700">
                      <SelectValue placeholder={t("events:form.placeholders.serviceStyle", { defaultValue: "Select style" })} />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_STYLE_OPTIONS.map((option) => (
                        <SelectItem key={option} value={option}>
                          {t(`events:serviceStyle.${option}`, { defaultValue: option })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-3">
            <div className="flex flex-col gap-2">
              <Label htmlFor="event-currency">{t("events:form.fields.currency", { defaultValue: "Currency" })}</Label>
              <Input id="event-currency" disabled={isSubmitting} {...form.register("currency")} />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="event-guests">{t("events:form.fields.guests", { defaultValue: "Guests" })}</Label>
              <Input
                id="event-guests"
                disabled={isSubmitting}
                inputMode="numeric"
                {...form.register("estimatedGuests")}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="event-budget">{t("events:form.fields.budget", { defaultValue: "Budget" })}</Label>
              <Input
                id="event-budget"
                disabled={isSubmitting}
                inputMode="decimal"
                {...form.register("budget")}
              />
            </div>
          </div>

          {mode === "create" ? (
            <div className="grid gap-3 md:grid-cols-2">
              <div className="flex flex-col gap-2">
                <Label htmlFor="event-deposit-amount">
                  {t("events:form.fields.depositAmount", { defaultValue: "Deposit amount" })}
                </Label>
                <Input
                  id="event-deposit-amount"
                  disabled={isSubmitting}
                  inputMode="decimal"
                  {...form.register("depositAmount")}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="event-deposit-due">
                  {t("events:form.fields.depositDue", { defaultValue: "Deposit due" })}
                </Label>
                <Input id="event-deposit-due" type="date" disabled={isSubmitting} {...form.register("depositDueDate")} />
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <Label htmlFor="event-deposit-due">
                {t("events:form.fields.depositDue", { defaultValue: "Deposit due" })}
              </Label>
              <Input id="event-deposit-due" type="date" disabled={isSubmitting} {...form.register("depositDueDate")} />
            </div>
          )}

          <div className="flex flex-col gap-2">
            <Label htmlFor="event-final-due">
              {t("events:form.fields.finalDue", { defaultValue: "Final payment due" })}
            </Label>
            <Input id="event-final-due" type="date" disabled={isSubmitting} {...form.register("finalPaymentDueDate")} />
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div className="flex flex-col gap-2">
              <Label htmlFor="event-contact-name">
                {t("events:form.fields.contactName", { defaultValue: "Point of contact" })}
              </Label>
              <Input id="event-contact-name" disabled={isSubmitting} {...form.register("contactName")} />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="event-contact-email">
                {t("events:form.fields.contactEmail", { defaultValue: "Contact email" })}
              </Label>
              <Input id="event-contact-email" type="email" disabled={isSubmitting} {...form.register("contactEmail")} />
              {form.formState.errors.contactEmail ? (
                <p className="text-sm text-rose-500">{form.formState.errors.contactEmail.message}</p>
              ) : null}
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div className="flex flex-col gap-2">
              <Label htmlFor="event-contact-phone">
                {t("events:form.fields.contactPhone", { defaultValue: "Contact phone" })}
              </Label>
              <Input id="event-contact-phone" disabled={isSubmitting} {...form.register("contactPhone")} />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="event-lead-source">
                {t("events:form.fields.leadSource", { defaultValue: "Lead source" })}
              </Label>
              <Input id="event-lead-source" disabled={isSubmitting} {...form.register("leadSource")} />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="event-notes">{t("events:form.fields.notes", { defaultValue: "Notes" })}</Label>
            <Textarea id="event-notes" disabled={isSubmitting} rows={4} {...form.register("notes")} />
          </div>

          <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {t("events:form.actions.cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {savingLabel}
                </>
              ) : (
                submitLabel
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export type { CateringEventFormValues };
